// src/components/GuidedSearchModal.js
import React, { useState, useEffect } from 'react';

const GuidedSearchModal = ({ isOpen, onClose, onSearch, filterOptions }) => {
  const [step, setStep] = useState(1);
  const [players, setPlayers] = useState('');
  const [maxTime, setMaxTime] = useState('');
  const [selectedThemes, setSelectedThemes] = useState([]);
  
  // Reset the form when the modal is opened
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setPlayers('');
      setMaxTime('');
      setSelectedThemes([]);
    }
  }, [isOpen]);

  // Handle closing the modal
  const handleClose = () => {
    onClose();
  };

  // Handle theme selection
  const toggleTheme = (theme) => {
    setSelectedThemes(prev => 
      prev.includes(theme) 
        ? []  // Clear selection if clicking the same theme
        : [theme]  // Only allow one theme
    );
  };

  // Handle final search
  const handleSearch = () => {
    onSearch({
      players,
      maxTime,
      themes: selectedThemes
    });
    onClose();
  };

  // Select the most popular themes (up to 12) for the second step
  const popularThemes = [
    "Abstract Strategy",
    "Adventure",
    "Animals",
    "Card Game",
    "Dice",
    "Fantasy",
    "Horror",
    "Miniatures",
    "Party Game",
    "Racing",
    "Science Fiction",
    "Wargame"
  ];

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    // Only close if the click is directly on the overlay, not its children
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4 overflow-hidden"
      onClick={handleOverlayClick}
    >
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-blue-400">Find Your Perfect Game</h2>
          <button 
            onClick={handleClose}
            className="text-gray-400 hover:text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {/* Step 1: Players and Time */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">How many players will you have?</h3>
                <div className="grid grid-cols-3 sm:grid-cols-3 gap-2">
                  {[1, 2, 3, 4, 5, "6+"].map(num => (
                    <button
                      key={num}
                      onClick={() => setPlayers(num === "6+" ? 6 : num)}
                      className={`py-3 px-4 rounded-lg border transition-colors ${
                        players === num || (num === "6+" && players === 6)
                          ? 'bg-blue-600 text-white border-blue-700' 
                          : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">How long do you want to play?</h3>
                <div className="grid grid-cols-2 sm:grid-cols-2 gap-2">
                  <button
                    onClick={() => setMaxTime(30)}
                    className={`py-3 px-4 rounded-lg border transition-colors ${
                      maxTime === 30
                        ? 'bg-blue-600 text-white border-blue-700' 
                        : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                    }`}
                  >
                    Quick (≤ 30 min)
                  </button>
                  <button
                    onClick={() => setMaxTime(60)}
                    className={`py-3 px-4 rounded-lg border transition-colors ${
                      maxTime === 60
                        ? 'bg-blue-600 text-white border-blue-700' 
                        : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                    }`}
                  >
                    Medium (≤ 60 min)
                  </button>
                  <button
                    onClick={() => setMaxTime(120)}
                    className={`py-3 px-4 rounded-lg border transition-colors ${
                      maxTime === 120
                        ? 'bg-blue-600 text-white border-blue-700' 
                        : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                    }`}
                  >
                    Long (≤ 2 hours)
                  </button>
                  <button
                    onClick={() => setMaxTime('')}
                    className={`py-3 px-4 rounded-lg border transition-colors ${
                      maxTime === ''
                        ? 'bg-blue-600 text-white border-blue-700' 
                        : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                    }`}
                  >
                    Any length
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Step 2: Themes */}
          {step === 2 && (
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Pick one thing you like:</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
                {popularThemes.map(theme => (
                  <button
                    key={theme}
                    onClick={() => toggleTheme(theme)}
                    className={`py-2 px-3 rounded-lg border text-sm transition-colors ${
                      selectedThemes.includes(theme)
                        ? 'bg-blue-600 text-white border-blue-700' 
                        : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                    }`}
                  >
                    {theme}
                  </button>
                ))}
              </div>
              
              <div className="bg-gray-700 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-blue-300 mb-2">Your selections</h4>
                <p className="text-gray-300">
                  <span className="font-medium">Players:</span> {players === '' ? 'Any' : players}
                  <br />
                  <span className="font-medium">Time:</span> {maxTime === '' ? 'Any' : `≤ ${maxTime} minutes`}
                  <br />
                  <span className="font-medium">Theme:</span> {selectedThemes.length > 0 ? selectedThemes[0] : 'Any'}
                </p>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 px-6 py-4 flex justify-between">
          {step === 1 ? (
            <>
              <button 
                onClick={handleClose}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Cancel
              </button>
              <button 
                onClick={() => setStep(2)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={players === ''}
              >
                Next
              </button>
            </>
          ) : (
            <>
              <button 
                onClick={() => setStep(1)}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Back
              </button>
              <button 
                onClick={handleSearch}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                Find Games
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuidedSearchModal;
